import {
  BaseQueryApi,
  BaseQueryFn,
  QueryReturnValue,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchArgs, FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { Variants } from 'common';
import jwtDecode from 'jwt-decode';
import { REHYDRATE } from 'redux-persist';

import {
  setAccessToken,
  setProviderName,
  setRefreshToken,
  setToken,
} from 'infrastructure/redux/slices/auth.slice';
import { clearUserData } from 'infrastructure/redux/slices/user.slice';
import { getReduxStoreVersion, RootState } from 'infrastructure/redux/store';
import { reset, triggerPlatformLogout } from 'infrastructure/redux/store/actions';

import { AuthRoutes } from './mboApiRoutes';

const mutex = new Mutex();

let webLogoutInFlight = false;

const handleWebLogoutRedirect = () => {
  if (!process.env.VITE_IS_WEB || typeof window === 'undefined') {
    return;
  }
  if (webLogoutInFlight) {
    return;
  }
  webLogoutInFlight = true;
  try {
    localStorage.removeItem(`persist:${getReduxStoreVersion()}`);
    localStorage.removeItem('new_email');
    localStorage.removeItem('email');
  } catch {
    // ignore storage access errors (private mode, etc.)
  }
  window.location.assign('/select-location');
};

const prepareHeaders = ({
  headers,
  token,
  variant,
}: {
  headers: Headers;
  token?: string;
  variant?: string;
}) => {
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (variant) {
    headers.set('X-Variant', variant);
  }

  return headers;
};

const resolveVariant = () => {
  if (process.env.VITE_IS_WEB) {
    return `${process.env.VITE_VARIANT || Variants.FIVEIRON}`;
  } else {
    return Variants.FIVEIRON_MOBILE;
  }
};

const logout = async (api: BaseQueryApi) => {
  // Wait for any platform-specific logout teardown (e.g. mobile awaits
  // LucraSDK.logout()). On web there is no native SDK to tear down; the
  // shared store still resolves this promise synchronously so this await
  // never hangs. See packages/frontend/src/infrastructure/redux/store/middleware.ts.
  await new Promise<void>((resolve) => {
    api.dispatch(triggerPlatformLogout(resolve));
  });

  // Clear all state
  api.dispatch(setToken(null));
  api.dispatch(setAccessToken(null));
  api.dispatch(setRefreshToken(null));
  api.dispatch(setProviderName(null));
  api.dispatch(clearUserData());

  // Reset the entire store state (including RTK Query cache)
  // This is critical when switching users to prevent stale data
  // See: https://github.com/reduxjs/redux-toolkit/issues/1622
  api.dispatch(reset());

  // On web the RTK Query layer has no access to the React Router tree, so a
  // failed refresh would otherwise leave the user stranded on a stale page
  // (e.g. booking calendar with every date disabled). Force a hard redirect
  // to /select-location so persisted/in-memory state is fully discarded.
  handleWebLogoutRedirect();
};

const isTokenExpired = (token: string, bufferInSeconds = 0) => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    if (typeof exp !== 'number') {
      return true;
    }
    const currentTime = Date.now() / 1000;
    return exp < currentTime + bufferInSeconds;
  } catch {
    // Malformed/corrupt token — treat as expired so the caller forces a logout
    return true;
  }
};

const refreshToken = async (api: BaseQueryApi): Promise<string | undefined> => {
  const storeState = api?.getState() as RootState;
  const baseUrl = getAPIBaseURL(storeState);
  const refreshToken = storeState.auth?.refreshToken as string;

  if (!refreshToken) {
    await logout(api);
    return;
  }

  if (isTokenExpired(refreshToken)) {
    await logout(api);
    return;
  }

  const refreshTokenQuery = fetchBaseQuery({
    baseUrl,
  });

  const refreshResult = (await refreshTokenQuery(
    {
      url: AuthRoutes.refreshToken(),
      method: 'POST',
      body: {
        refreshToken: refreshToken,
      },
    },
    api,
    {},
  )) as QueryReturnValue<{ accessToken: string; refreshToken: string }>;

  if (refreshResult.error) {
    await logout(api);
    return;
  }

  const isUserVerified = storeState.auth.token || storeState.auth.providerName;
  if (refreshResult.data && isUserVerified) {
    api.dispatch(setRefreshToken(refreshResult.data.refreshToken));
    api.dispatch(setAccessToken(refreshResult.data.accessToken));
    return refreshResult.data.accessToken;
  } else {
    await logout(api);
  }
};

const getAPIBaseURL = (storeState: RootState) => {
  try {
    if (process.env.VITE_IS_WEB) {
      if (process.env.NODE_ENV === 'development') {
        return '/api/';
      }
      return process.env.VITE_BACKEND_API_BASE_URL;
    } else {
      const apiBaseURL = storeState?.auth?.apiBaseURL;
      if (apiBaseURL) {
        return apiBaseURL;
      }

      // We need to use require here because Expo constants are not available in the browser.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Constants = require('expo-constants').default;
      const fallbackUrl = Constants.expoConfig?.extra?.apiHost;
      if (!fallbackUrl) {
        console.warn('[getAPIBaseURL] apiBaseURL is undefined and no fallback found');
      }
      return fallbackUrl;
    }
  } catch {
    return undefined;
  }
};

const API_TIMEOUT_MS = 30_000;

export const getDefaultFetchBaseQueryArgs: (
  baseUrl?: string,
  token?: string | undefined,
  variant?: string,
) => FetchBaseQueryArgs = (baseUrl, token, variant) => ({
  baseUrl,
  timeout: API_TIMEOUT_MS,
  prepareHeaders: (headers: Headers): Headers =>
    prepareHeaders({
      headers,
      token,
      variant,
    }),
});

export const baseQueryWithReauth: (
  customFetchBaseQueryArgs?: FetchBaseQueryArgs,
) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  (customFetchBaseQueryArgs) => async (args, api, extraOptions) => {
    const storeState = api.getState() as RootState;
    const baseUrl = getAPIBaseURL(storeState);
    const variant = resolveVariant();
    let accessToken = storeState?.auth?.accessToken;

    const handleTokenRefresh = async () => {
      // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          return await refreshToken(api);
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        return (api.getState() as RootState)?.auth?.accessToken;
      }
    };

    if (accessToken && isTokenExpired(accessToken, 60)) {
      accessToken = await handleTokenRefresh();
    }

    const baseQueryFn = fetchBaseQuery(
      customFetchBaseQueryArgs || getDefaultFetchBaseQueryArgs(baseUrl, accessToken, variant),
    );

    const result = await baseQueryFn(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      accessToken = await handleTokenRefresh();

      if (accessToken) {
        return await baseQueryFn(args, api, extraOptions);
      } else {
        await logout(api);
      }
    }

    return result;
  };

export const baseApi = createApi({
  reducerPath: 'mboAPI',
  keepUnusedDataFor: 60,
  baseQuery: baseQueryWithReauth(),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.payload && action.type === REHYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: () => ({}),
});

export default baseApi;
